import { Heart, BookOpen, Sparkles, Shield } from "lucide-react";
import { ASSETS } from "../lib/api";

export default function Volunteer() {
  return (
    <div className="no-img-drag">
      {/* HERO with provided poster */}
      <section className="bg-gradient-to-b from-[#FCE7EE] to-brand-creamSoft border-b border-brand-beige">
        <div className="max-w-7xl mx-auto container-x py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-brand-pink font-bold text-xs uppercase tracking-[0.2em] mb-3">Voluntariat · School of Dreams</p>
            <h1 className="font-display font-extrabold text-brand-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-xl text-balance">
              Împreună pentru sănătate orală — <span className="text-brand-pink italic font-medium">pentru toți copiii</span>
            </h1>
            <p className="mt-6 text-brand-navy/75 text-lg max-w-xl font-light leading-relaxed">
              Ca voluntar în cadrul proiectului <strong>School of Dreams</strong>, susțin educația pentru sănătatea orală a copiilor — prin ateliere practice, demonstrații și programe de prevenție desfășurate în școli.
            </p>
            <a
              href="https://www.startline.ro"
              target="_blank" rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-pink text-white font-semibold hover:opacity-90"
              data-testid="school-of-dreams-link"
            >
              <Heart className="w-4 h-4" /> www.startline.ro
            </a>
          </div>
          <div>
            <img
              src={ASSETS.volunteerPoster}
              alt="Împreună pentru sănătate orală pentru toți"
              className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              draggable={false}
              data-testid="volunteer-poster"
            />
          </div>
        </div>
      </section>

      {/* Workshop image + intro */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto container-x py-20 grid lg:grid-cols-2 gap-12 items-center">
          <img
            src={ASSETS.volunteerWorkshop}
            alt="Atelier de educație pentru sănătatea orală cu Dr. Biriș"
            className="rounded-2xl shadow-soft w-full"
            draggable={false}
            data-testid="volunteer-workshop-image"
          />
          <div>
            <h2 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl tracking-tight">Educația și prevenția — fundamentul sănătății orale</h2>
            <p className="mt-5 text-brand-navy/80 text-lg leading-relaxed font-light">
              <em>„Educația și prevenția merg mână în mână cu încrederea în sine!"</em> — acesta este principiul care îmi ghidează activitatea de voluntariat. Un copil care învață de la o vârstă fragedă să își îngrijească dinții devine un adult cu sănătate orală echilibrată și cu mai multă încredere în sine.
            </p>
            <p className="mt-4 text-brand-navy/75 leading-relaxed font-light">
              Atelierele includ povești și demonstrații practice despre periajul corect, alimentația sănătoasă și importanța vizitei periodice la medicul stomatolog, însoțite de jocuri și materiale educative adaptate vârstei.
            </p>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="bg-brand-creamSoft border-y border-brand-beige">
        <div className="max-w-7xl mx-auto container-x py-20">
          <div className="max-w-2xl mb-12">
            <p className="text-brand-pink font-bold text-xs uppercase tracking-[0.2em] mb-3">Cei trei piloni</p>
            <h2 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl tracking-tight">Educație · Demonstrație · Prevenție</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Pillar
              icon={BookOpen}
              color="bg-[#216C72]/10 text-brand-navy"
              title="Educație privind igiena orală"
              text="Îi învățăm pe copii cât de importantă este igiena orală zilnică pentru sănătatea dinților și a întregului organism."
            />
            <Pillar
              icon={Sparkles}
              color="bg-[#7AA53C]/15 text-brand-green"
              title="Demonstrații practice despre periajul corect"
              text="Arătăm pas cu pas cum se face periajul corect, printr-o tehnică simplă și ușor de aplicat acasă."
            />
            <Pillar
              icon={Shield}
              color="bg-[#D88FA3]/15 text-brand-pink"
              title="Prevenție și conștientizare"
              text="Prevenim afecțiunile dentare prin informare, alimentație sănătoasă și controale stomatologice regulate."
            />
          </div>
        </div>
      </section>

      {/* Closing band */}
      <section className="bg-brand-navy text-white">
        <div className="max-w-5xl mx-auto container-x py-14 text-center">
          <p className="text-brand-yellow font-semibold text-xs uppercase tracking-[0.2em] mb-3">Misiune</p>
          <p className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Fiecare zâmbet contează. Fiecare copil contează.</p>
          <p className="mt-3 text-white/75 italic font-light">Împreună putem face o diferență.</p>
        </div>
      </section>
    </div>
  );
}

function Pillar({ icon: Icon, color, title, text }) {
  return (
    <div className="bg-white rounded-2xl p-7 border border-brand-beige hover:shadow-soft transition-shadow">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="mt-5 font-display font-bold text-brand-navy text-lg leading-tight">{title}</h3>
      <p className="mt-3 text-sm text-brand-navy/70 leading-relaxed">{text}</p>
    </div>
  );
}
